import React, { useEffect, useRef, useState } from 'react';
import { FaUser, FaBullseye, FaCogs, FaCode, FaCloud, FaShieldAlt, FaRocket, FaChartLine, FaDatabase, FaNetworkWired, FaSun, FaMoon } from 'react-icons/fa';
import { SiTerraform, SiKubernetes, SiJenkins, SiPrometheus, SiGrafana, SiLinux } from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';
import '../index.css';


const Profile = () => {
  const containerRef = useRef(null);
  const cardsRef = useRef([]);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-card-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  const expertiseAreas = [
    {
      icon: <SiTerraform className="h-10 w-10" />,
      title: 'Infrastructure as Code',
      description: 'Terraform, CloudFormation, AWS CDK',
      color: 'from-purple-500 to-pink-500',
      darkColor: 'from-purple-400 to-pink-400',
      delay: '100'
    },
    {
      icon: <SiKubernetes className="h-10 w-10" />,
      title: 'Container Orchestration',
      description: 'Kubernetes, Docker, EKS, ECS',
      color: 'from-blue-500 to-cyan-500',
      darkColor: 'from-blue-400 to-cyan-400',
      delay: '200'
    },
    {
      icon: <SiJenkins className="h-10 w-10" />,
      title: 'CI/CD Automation',
      description: 'Jenkins, GitHub Actions, GitLab CI',
      color: 'from-red-500 to-orange-500',
      darkColor: 'from-red-400 to-orange-400',
      delay: '300'
    },
    {
      icon: <FaShieldAlt className="h-10 w-10" />,
      title: 'Security & Compliance',
      description: 'IAM, Security Groups, Compliance',
      color: 'from-green-500 to-emerald-500',
      darkColor: 'from-green-400 to-emerald-400',
      delay: '400'
    },
    {
      icon: <SiPrometheus className="h-10 w-10" />,
      title: 'Monitoring & Observability',
      description: 'Prometheus, Grafana, CloudWatch',
      color: 'from-orange-500 to-red-500',
      darkColor: 'from-orange-400 to-red-400',
      delay: '500'
    },
    {
      icon: <FaDatabase className="h-10 w-10" />,
      title: 'Database Management',
      description: 'RDS, DynamoDB, Redis, Postgres',
      color: 'from-indigo-500 to-purple-500',
      darkColor: 'from-indigo-400 to-purple-400',
      delay: '600'
    }
  ];

  const stats = [
    { value: '3+', label: 'Years Experience', icon: '📅', bgColor: 'from-blue-500 to-cyan-500' },
    { value: '50+', label: 'Projects Deployed', icon: '🚀', bgColor: 'from-purple-500 to-pink-500' },
    { value: '99.9%', label: 'Uptime Maintained', icon: '⚡', bgColor: 'from-green-500 to-emerald-500' },
    { value: '1000+', label: 'Containers Managed', icon: '📦', bgColor: 'from-orange-500 to-yellow-500' },
  ];

  const technicalSkills = [
    { skill: 'AWS Cloud Services', level: 'Expert', color: 'bg-orange-500', textColor: 'text-orange-600 dark:text-orange-400' },
    { skill: 'Kubernetes & Docker', level: 'Expert', color: 'bg-blue-500', textColor: 'text-blue-600 dark:text-blue-400' },
    { skill: 'Terraform & IaC', level: 'Expert', color: 'bg-purple-500', textColor: 'text-purple-600 dark:text-purple-400' },
    { skill: 'CI/CD Pipelines', level: 'Advanced', color: 'bg-red-500', textColor: 'text-red-600 dark:text-red-400' },
    { skill: 'Linux Administration', level: 'Expert', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400' },
    { skill: 'Python & Shell Scripting', level: 'Advanced', color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400' },
    { skill: 'Monitoring & Alerting', level: 'Advanced', color: 'bg-pink-500', textColor: 'text-pink-600 dark:text-pink-400' },
    { skill: 'Security Best Practices', level: 'Advanced', color: 'bg-indigo-500', textColor: 'text-indigo-600 dark:text-indigo-400' },
  ];

  return (
    <section 
      id="profile" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode 
          ? 'bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900' 
          : 'bg-gradient-to-b from-white via-white to-white'
      }`}
    >
       <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements - Simplified */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/5 to-cyan-300/5'
        }`}></div>
        <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/5 to-pink-300/5'
        }`}></div>
        
        {/* Animated dots */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full animate-pulse ${
              darkMode ? 'bg-blue-400/10' : 'bg-blue-400/20'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaUser className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">👨‍💻</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Professional </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Profile
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'DevOps Specialist',
                2000,
                'Cloud Engineer',
                2000,
                'Infrastructure Expert',
                2000,
                'Automation Wizard',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Main Profile Card */}
        <div className="relative mb-16">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 rounded-3xl blur opacity-30 animate-pulse"></div>
          <div className={`relative rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border ${
            darkMode 
              ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border-gray-700/50' 
              : 'bg-gradient-to-br from-white to-gray-50 border-white/50'
          }`}>
            <div className="p-8 md:p-12">
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="lg:w-2/3 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="mt-2">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
                        <FaBullseye className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        DevOps & Cloud Infrastructure Specialist
                      </h3>
                      <div className={`leading-relaxed text-lg space-y-4 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <p>
                          <span className={`font-semibold ${
                            darkMode ? 'text-blue-400' : 'text-blue-600'
                          }`}>DevOps Engineer</span> with extensive hands-on experience in designing, implementing, and maintaining 
                          <span className={`font-semibold ${
                            darkMode ? 'text-purple-400' : 'text-purple-600'
                          }`}> cloud-native infrastructure</span> and production environments.
                        </p>
                        <p>
                          Expert in <span className={`font-semibold ${
                            darkMode ? 'text-green-400' : 'text-green-600'
                          }`}>AWS cloud services</span>, container orchestration with 
                          <span className={`font-semibold ${
                            darkMode ? 'text-blue-400' : 'text-blue-600'
                          }`}> Kubernetes</span>, and infrastructure automation using 
                          <span className={`font-semibold ${
                            darkMode ? 'text-orange-400' : 'text-orange-600'
                          }`}> Terraform</span>.
                        </p>
                        <p>
                          Passionate about building <span className={`font-semibold ${
                            darkMode ? 'text-cyan-400' : 'text-cyan-600'
                          }`}>scalable, reliable, and secure</span> cloud 
                          environments that can handle real-world production workloads with optimal performance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="lg:w-1/3">
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, index) => (
                      <div 
                        key={index}
                        ref={el => cardsRef.current[index] = el}
                        className={`opacity-0 transform translate-y-8 backdrop-blur-sm rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border ${
                          darkMode 
                            ? 'bg-gray-800/50 border-gray-700' 
                            : 'bg-white/80 border-gray-100'
                        }`}
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="text-3xl mb-2">{stat.icon}</div>
                        <div className={`text-2xl font-bold bg-gradient-to-r ${stat.bgColor} bg-clip-text text-transparent`}>
                          {stat.value}
                        </div>
                        <div className={`text-sm mt-1 ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expertise Areas */}
        <div className="mb-12">
          <h3 className={`text-2xl md:text-3xl font-bold text-center mb-8 ${
            darkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Core <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Expertise</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertiseAreas.map((area, index) => (
              <div
                key={index}
                ref={el => cardsRef.current[stats.length + index] = el}
                className={`opacity-0 transform translate-y-8 group relative overflow-hidden backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border ${
                  darkMode 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'bg-white/80 border-gray-100'
                }`}
                style={{ animationDelay: `${area.delay}ms` }}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  darkMode ? area.darkColor : area.color
                } opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                
                {/* Icon with gradient background */}
                <div className={`relative mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br ${
                  darkMode ? area.darkColor : area.color
                } text-white transform group-hover:scale-110 transition-transform duration-500`}>
                  {area.icon}
                </div>
                
                {/* Title and description */}
                <h4 className={`text-xl font-bold mb-3 relative z-10 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>{area.title}</h4>
                <p className={`relative z-10 ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>{area.description}</p>
                
                {/* Hover effect line */}
                <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${
                  darkMode ? area.darkColor : area.color
                } transition-all duration-500 group-hover:w-full`}></div>
                
                {/* Floating dots */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="flex space-x-1">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${
                      area.color.includes('blue') ? 'bg-blue-500' : 
                      area.color.includes('purple') ? 'bg-purple-500' : 
                      area.color.includes('red') ? 'bg-red-500' : 
                      area.color.includes('green') ? 'bg-green-500' : 
                      'bg-orange-500'
                    }`}></div>
                    <div className={`w-2 h-2 rounded-full animate-pulse ${
                      area.color.includes('cyan') ? 'bg-cyan-500' : 
                      area.color.includes('pink') ? 'bg-pink-500' : 
                      area.color.includes('orange') ? 'bg-orange-500' : 
                      area.color.includes('emerald') ? 'bg-emerald-500' : 
                      'bg-red-500'
                    }`} style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Skills Banner */}
        <div className={`relative overflow-hidden rounded-2xl border backdrop-blur-sm ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-600/10 via-cyan-500/10 to-purple-600/10 border-gray-700/30' 
            : 'bg-gradient-to-r from-blue-600/5 via-cyan-500/5 to-purple-600/5 border-blue-200/30'
        }`}>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500"></div>
          
          <div className="p-6 md:p-8">
            <h4 className={`text-xl font-bold text-center mb-6 ${
              darkMode ? 'text-gray-100' : 'text-gray-800'
            }`}>Key Technical Skills</h4>
            <div className="flex flex-wrap justify-center gap-3">
              {technicalSkills.map((skill, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 px-4 py-2 backdrop-blur-sm rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 ${
                    darkMode 
                      ? 'bg-gray-800/50 border-gray-700' 
                      : 'bg-white/80 border-gray-100'
                  }`}
                >
                  <div className={`w-2 h-2 ${skill.color} rounded-full animate-pulse`}></div>
                  <span className={`font-medium ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{skill.skill}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>{skill.level}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes card-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-card-in {
          animation: card-in 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Profile;